import { useMemo, useState } from "react";
import { Notify, toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useMachineEventReasons, useJustifyEvent } from "./hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";

interface Props {
  eventId: string | null;
  open: boolean;
  onClose: () => void;
}

export function JustifyEventDialog({ eventId, open, onClose }: Props) {
  const [reasonSlug, setReasonSlug] = useState<string>("");
  const [reasonOtherDesc, setReasonOtherDesc] = useState<string>("");
  const reasonsQuery = useMachineEventReasons();
  const { data: reasons = [], isLoading: loadingReasons } = reasonsQuery;
  const justify = useJustifyEvent();

  const grouped = useMemo(() => {
    const byCategory = new Map<string, typeof reasons>();
    for (const r of reasons) {
      const key = r.category ?? "—";
      const arr = byCategory.get(key) ?? [];
      arr.push(r);
      byCategory.set(key, arr);
    }
    return Array.from(byCategory.entries());
  }, [reasons]);

  if (!open || !eventId) return null;

  const catalogEmpty = !loadingReasons && reasons.length === 0;

  async function submit() {
    if (!reasonSlug) {
      toast.error("Motivo obrigatório.", { description: "Selecione um motivo do catálogo." });
      return;
    }
    try {
      await justify.mutateAsync({
        eventId: eventId!,
        data: {
          reasonSlug,
          reasonOtherDesc: reasonOtherDesc.trim() || undefined,
        },
      });
      toast.success("Evento justificado.");
      setReasonSlug("");
      setReasonOtherDesc("");
      onClose();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao justificar evento.", { description: msg });
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- backdrop overlay; Escape fecha via window listener
    <div
      className="fixed inset-0 z-[60] flex animate-fade-in items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md animate-scale-in">
        <CardContent className="space-y-3 pt-6">
          <h2 className="text-base font-semibold">Justificar evento de parada</h2>
          <p className="text-xs text-muted-foreground">
            Selecione o motivo no catálogo abaixo. A descrição é opcional e fica registrada junto ao
            evento para auditoria.
          </p>

          {reasonsQuery.isError ? (
            <Notify.QueryError
              variant="inline"
              error={reasonsQuery.error}
              onRetry={() => void reasonsQuery.refetch()}
              title="Não foi possível carregar o catálogo de motivos"
            />
          ) : catalogEmpty ? (
            <div
              className="border-rd-warning-500/30 rounded border bg-rd-warning-50 p-3 text-sm"
              role="alert"
            >
              <strong className="font-semibold">Catálogo de motivos indisponível.</strong>
              <p className="mt-1 text-xs text-muted-foreground">
                Acione o suporte. Não é possível justificar o evento até o catálogo voltar.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="jed-reason">
                  Motivo <span className="text-rd-danger-600">*</span>
                </Label>
                <Select
                  id="jed-reason"
                  value={reasonSlug}
                  onChange={(e) => setReasonSlug(e.target.value)}
                  disabled={loadingReasons}
                  required
                  aria-required="true"
                >
                  <option value="">— Selecione —</option>
                  {grouped.map(([category, items]) => (
                    <optgroup key={category} label={category}>
                      {items.map((r) => (
                        <option key={r.slug} value={r.slug}>
                          {r.displayName}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="jed-desc">Descrição (opcional)</Label>
                <Input
                  id="jed-desc"
                  value={reasonOtherDesc}
                  onChange={(e) => setReasonOtherDesc(e.target.value)}
                  placeholder="Contexto adicional, número de chamado, responsável…"
                  maxLength={500}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={justify.isPending || catalogEmpty || loadingReasons || reasonsQuery.isError}
            >
              {justify.isPending ? "Justificando…" : "Justificar"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
